import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { sprintf } from 'sprintf-js';

import { apiFetchUserByEmail } from 'api/apiFetchUserByEmail';
import { ListingApplicationPayload, validateApplication } from 'api/ats';
import { ApiListingApplicationListingType } from 'api/types/ApiListingApplicationListingType';
import { ApiRequestedAttachment } from 'api/types/ApiRequestedAttachment';
import { FormUsageContext } from 'components/Form/Form';
import { Text } from 'components/Text/Text';
import { useTemporaryStorage } from 'hooks/useTemporaryStorage';
import { userDashboardCreateListingApplication } from 'modules/userDashboard/zustand-stores/userDashboardCurrentListingApplicationsStore';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { LISTING_TYPE_HUMAN_NAME_MAP } from 'utils/constants/general/listingTypeHumanNameMap';
import { OrgType } from 'utils/constants/general/orgTypes';
import { isDefined } from 'utils/functional/type';
import { hasResumeDb } from 'utils/resumeDatabase';
import { getFirstParam } from 'utils/url/getFirstParam';
import { showModal } from 'zustand-stores/modalStore';
import { useUserEnvironment } from 'zustand-stores/userEnvironmentStore';

import { ListingViewApplicationFormContainer } from './ListingViewApplicationFormContainer';

type Props = {
  analyticsTitle: string;
  usageContext: FormUsageContext;
  listingId: string;
  listingType: ApiListingApplicationListingType;
  orgId?: string;
  orgType?: OrgType;
  requestedAttachments: ApiRequestedAttachment[];
  requestedAttachmentsHash: string;
  isPreview?: boolean;
};

export function ListingViewApplicationForm({
  analyticsTitle,
  usageContext,
  isPreview,
  listingId,
  listingType,
  orgId,
  orgType,
  requestedAttachments,
  requestedAttachmentsHash,
}: Props) {
  const navigate = useNavigate();
  const [temporaryStorageSubmitted, setTemporaryStorageSubmitted] =
    useState(false);
  const userEnvironment = useUserEnvironment();
  const { user, environment } = userEnvironment;

  const [saveTemporaryStorage, temporaryStorage] = useTemporaryStorage(
    user?.emailVerified && typeof window !== 'undefined'
      ? getFirstParam(window.location, 'tmpId')
      : '',
  );
  const formRef = useRef<HTMLFormElement>(null);

  const initialValues = useMemo(
    () =>
      'initialValues' in temporaryStorage
        ? temporaryStorage.initialValues
        : {
            requestedAttachmentsHash,
          },
    [requestedAttachmentsHash, temporaryStorage],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const createApplicationWrapper = ({ values }: any) =>
    userDashboardCreateListingApplication({ ...values }).then(() => {
      trackEvent('Success: Applied to Listing', {
        listing_id: listingId,
        listing_type: listingType,
        org_id: orgId || null,
        org_type: orgType || null,
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        is_new_resume: user.resumeId !== values.resumeS3FileId,
      });
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      return showOnboarding
        ? navigate('/dashboard/employment-profile/get-discovered?applied=true')
        : navigate('/dashboard/application-history', {
            state: {
              flashMessage: {
                type: 'LISTING_APPLICATION_SUBMITTED',
              },
            },
          });
    });

  useEffect(() => {
    if (
      initialValues &&
      // requestedAttachmentsHash is always there
      Object.keys(initialValues).length > 1 &&
      !temporaryStorageSubmitted &&
      formRef.current
    ) {
      setTemporaryStorageSubmitted(true);
      formRef.current.dispatchEvent(new Event('submit', { bubbles: true }));
    }
  }, [temporaryStorage, temporaryStorageSubmitted, initialValues]);
  const showOnboarding =
    !isDefined(user?.isRecruitable) && hasResumeDb(user, environment);

  const onSubmit = ({
    handleError,
    values,
    updateSubmitting,
  }: {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handleError: (...args: Array<any>) => any;
    values: ListingApplicationPayload;
    // ListingApplicationPayload,
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateSubmitting: (...args: Array<any>) => any;
  }) => {
    if (!user) {
      return validateApplication(values)
        .then(() => {
          Promise.all([
            saveTemporaryStorage([
              {
                type: 'LISTING_APPLICATION_VALUES',
                data: values,
              },
            ]),
            values.email && apiFetchUserByEmail(values.email),
          ])
            .then(([id, userExists]) => {
              updateSubmitting(false);
              const redirectTo = `${window.location.pathname}${
                window.location.search
              }${window.location.search ? '&' : '?'}tmpId=${id || ''}`;

              if (userExists) {
                return showModal(
                  'LOGIN',
                  // @ts-expect-error TS(2322): Type 'string | null | undefined' is not assignable... Remove this comment to see the full error message
                  { redirectTo, email: values.email },
                );
              }

              return showModal('SIGNUP', {
                title: getText('Just One More Step!'),
                description: (
                  <Text mb={4}>
                    {sprintf(
                      getText(
                        'Create an account on Idealist in order to finish applying to this %(listingType)s.',
                      ),
                      {
                        listingType: LISTING_TYPE_HUMAN_NAME_MAP[listingType],
                      },
                    )}
                  </Text>
                ),
                // @ts-expect-error TS(2322): Type 'string | null | undefined' is not assignable... Remove this comment to see the full error message
                firstName: values.firstName,
                // @ts-expect-error TS(2322): Type 'string | null | undefined' is not assignable... Remove this comment to see the full error message
                lastName: values.lastName,
                // @ts-expect-error TS(2322): Type 'string | null | undefined' is not assignable... Remove this comment to see the full error message
                email: values.email,
                redirectTo,
              });
            })
            .catch(handleError);
        })
        .catch(handleError);
    }
    return createApplicationWrapper({
      values,
    }).catch(handleError);
  };

  return (
    <ListingViewApplicationFormContainer
      isPreview={isPreview}
      analyticsTitle={analyticsTitle}
      usageContext={usageContext}
      formRef={formRef}
      onSubmit={onSubmit}
      listingType={listingType}
      listingId={listingId}
      requestedAttachments={requestedAttachments}
      initialValues={initialValues}
    />
  );
}
